/*
  MedicalHistoryPanel — spec §11 structured history for a client.

  Categories: condition · surgery · fracture · fall · cardiovascular ·
  respiratory · neurological · musculoskeletal · metabolic · oncology ·
  mental_health · obstetric · device · other.

  Each entry captures onset, status (active/resolved/monitoring),
  severity, treating clinician, exercise implications, source
  (client-reported vs clinician-confirmed) and a last_confirmed_date
  so practitioners can see how stale an entry is.

  Placed under the Medical Alerts strip on the Health Record tab.
  Owner/manager can archive; any signed-in staff can create + confirm
  + edit their own or team entries.
*/
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  History, Plus, Pencil, CheckCircle2, ArchiveRestore, Loader2, Stethoscope,
  Activity, Heart, Wind, Brain, Bone, Droplet, Ribbon, Sparkles, Baby,
  Cpu, Circle, HelpCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";

const CATEGORIES = [
  { value: "condition",       label: "General condition",  icon: Stethoscope },
  { value: "surgery",         label: "Surgery",             icon: Activity },
  { value: "fracture",        label: "Fracture",            icon: Bone },
  { value: "fall",            label: "Fall",                icon: ArchiveRestore },
  { value: "cardiovascular",  label: "Cardiovascular",      icon: Heart },
  { value: "respiratory",     label: "Respiratory",         icon: Wind },
  { value: "neurological",    label: "Neurological",        icon: Brain },
  { value: "musculoskeletal", label: "Musculoskeletal",     icon: Bone },
  { value: "metabolic",       label: "Metabolic",           icon: Droplet },
  { value: "oncology",        label: "Oncology",            icon: Ribbon },
  { value: "mental_health",   label: "Mental health",       icon: Sparkles },
  { value: "obstetric",       label: "Obstetric",           icon: Baby },
  { value: "device",          label: "Device (implanted/assistive)", icon: Cpu },
  { value: "other",           label: "Other",               icon: HelpCircle },
];
const CAT_MAP = Object.fromEntries(CATEGORIES.map((c) => [c.value, c]));

const STATUS_TONES = {
  active:     "bg-rose-50 text-rose-800 border-rose-200",
  monitoring: "bg-amber-50 text-amber-800 border-amber-200",
  resolved:   "bg-zinc-100 text-zinc-600 border-zinc-200",
};
const SEVERITY_TONES = {
  mild:     "text-emerald-700",
  moderate: "text-amber-700",
  severe:   "text-red-700",
};

const SOURCES = [
  { value: "client_reported",     label: "Client-reported" },
  { value: "clinician_confirmed", label: "Clinician-confirmed" },
  { value: "imported",            label: "Imported" },
  { value: "migrated",            label: "Migrated" },
];

function fmtDate(d) {
  if (!d) return "—";
  try {
    const parsed = new Date(d);
    if (!Number.isNaN(parsed.getTime())) return parsed.toLocaleDateString();
  } catch { /* fall through */ }
  return String(d);
}

export default function MedicalHistoryPanel({ email }) {
  const { can } = useAuth();
  const canArchive = can("owner", "manager");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmingId, setConfirmingId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");   // all | active | resolved | monitoring

  const encoded = encodeURIComponent(email || "");

  const load = useCallback(async () => {
    if (!email) return;
    setLoading(true);
    try {
      const r = await api.get(`/clients/${encoded}/medical-history`);
      setEntries(r.data?.entries || []);
    } catch (e) {
      toast.error("Couldn't load medical history", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, [email, encoded]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (statusFilter === "all") return entries;
    return entries.filter((e) => e.status === statusFilter);
  }, [entries, statusFilter]);

  const counts = useMemo(() => ({
    all: entries.length,
    active: entries.filter((e) => e.status === "active").length,
    monitoring: entries.filter((e) => e.status === "monitoring").length,
    resolved: entries.filter((e) => e.status === "resolved").length,
  }), [entries]);

  async function confirmEntry(id) {
    setConfirmingId(id);
    try {
      await api.post(`/medical-history/${id}/confirm`);
      toast.success("Marked as still accurate today");
      await load();
    } catch (e) {
      toast.error("Couldn't confirm", { description: e?.response?.data?.detail || e.message });
    } finally {
      setConfirmingId(null);
    }
  }

  async function archiveEntry(id) {
    if (!confirm("Archive this entry? Owner/manager can restore it later; it stays in the audit trail.")) return;
    try {
      await api.delete(`/medical-history/${id}`);
      toast.success("Archived");
      await load();
    } catch (e) {
      toast.error("Couldn't archive", { description: e?.response?.data?.detail || e.message });
    }
  }

  return (
    <div className="space-y-3" data-testid="medical-history-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold flex items-center gap-1.5">
          <History className="h-3.5 w-3.5" /> Medical history
          {counts.all > 0 && (
            <span className="text-zinc-400 normal-case font-normal">
              · {counts.active} active
              {counts.monitoring > 0 && ` · ${counts.monitoring} monitoring`}
              {counts.resolved > 0 && ` · ${counts.resolved} resolved`}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <StatusChip current={statusFilter} value="all" label={`All (${counts.all})`} onClick={setStatusFilter} />
          <StatusChip current={statusFilter} value="active" label={`Active (${counts.active})`} onClick={setStatusFilter} />
          {counts.monitoring > 0 && <StatusChip current={statusFilter} value="monitoring" label={`Monitoring (${counts.monitoring})`} onClick={setStatusFilter} />}
          {counts.resolved > 0 && <StatusChip current={statusFilter} value="resolved" label={`Resolved (${counts.resolved})`} onClick={setStatusFilter} />}
          <Button
            size="sm" variant="outline"
            onClick={() => { setEditing(null); setDialogOpen(true); }}
            data-testid="mh-add-btn"
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Add entry
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-xs text-zinc-500 py-3 flex items-center gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-xs text-zinc-500 italic border border-dashed border-zinc-200 rounded-md p-3">
          {entries.length === 0
            ? "No medical history on file. Add conditions, past surgery, fractures, falls, or other clinically-relevant events."
            : "Nothing matches this filter."}
        </div>
      ) : (
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="mh-list">
          {filtered.map((e) => (
            <HistoryRow
              key={e.id}
              entry={e}
              busy={confirmingId === e.id}
              canArchive={canArchive}
              onEdit={() => { setEditing(e); setDialogOpen(true); }}
              onConfirm={() => confirmEntry(e.id)}
              onArchive={() => archiveEntry(e.id)}
            />
          ))}
        </ul>
      )}

      {dialogOpen && (
        <MedicalHistoryDialog
          email={email}
          existing={editing}
          onClose={() => { setDialogOpen(false); setEditing(null); }}
          onSaved={load}
        />
      )}
    </div>
  );
}

function StatusChip({ current, value, label, onClick }) {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      className={cn(
        "text-2xs px-2 py-1 rounded-md border transition-colors",
        active
          ? "bg-zinc-950 border-zinc-950 text-white"
          : "border-zinc-300 text-zinc-600 hover:bg-zinc-100"
      )}
      data-testid={`mh-filter-${value}`}
    >
      {label}
    </button>
  );
}

function HistoryRow({ entry, busy, canArchive, onEdit, onConfirm, onArchive }) {
  const cat = CAT_MAP[entry.category] || CAT_MAP.condition;
  const Icon = cat.icon || Circle;
  const status = entry.status || "active";
  return (
    <li
      className="rounded-md border border-zinc-200 bg-white p-3 space-y-1.5 hover:border-zinc-300 transition-colors"
      data-testid={`mh-row-${entry.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0 flex-1">
          <Icon className="h-4 w-4 mt-0.5 text-zinc-600 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-zinc-950 leading-tight break-words">{entry.title}</div>
            <div className="text-2xs text-zinc-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
              <span>{cat.label}</span>
              {entry.onset_date && (<><span>·</span><span>Onset {fmtDate(entry.onset_date)}</span></>)}
              {entry.severity && (
                <>
                  <span>·</span>
                  <span className={cn("font-medium capitalize", SEVERITY_TONES[entry.severity] || "text-zinc-600")}>
                    {entry.severity}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
        <span className={cn(
          "text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border font-medium capitalize shrink-0",
          STATUS_TONES[status] || STATUS_TONES.active,
        )}>
          {status}
        </span>
      </div>

      {(entry.exercise_implications || entry.notes) && (
        <div className="text-[0.75rem] text-zinc-700 space-y-0.5">
          {entry.exercise_implications && (
            <div>
              <span className="text-2xs uppercase tracking-wider text-zinc-400 mr-1">EX impl.</span>
              {entry.exercise_implications}
            </div>
          )}
          {entry.notes && (
            <div className="text-zinc-600">{entry.notes}</div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between text-2xs text-zinc-500 pt-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          {entry.treating_clinician && <span>Dr {entry.treating_clinician}</span>}
          <span className="text-zinc-400">·</span>
          <span className="capitalize">{entry.source?.replace("_", "-")}</span>
          {entry.last_confirmed_date && (
            <>
              <span className="text-zinc-400">·</span>
              <span title="Last confirmed as still accurate">Confirmed {fmtDate(entry.last_confirmed_date)}</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="p-1 rounded hover:bg-emerald-50 text-emerald-700 disabled:opacity-50"
            title="Mark still accurate today"
            data-testid={`mh-confirm-${entry.id}`}
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
          </button>
          <button
            type="button"
            onClick={onEdit}
            className="p-1 rounded hover:bg-zinc-100 text-zinc-600"
            title="Edit"
            data-testid={`mh-edit-${entry.id}`}
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          {canArchive && (
            <button
              type="button"
              onClick={onArchive}
              className="p-1 rounded hover:bg-red-50 text-red-700"
              title="Archive"
              data-testid={`mh-archive-${entry.id}`}
            >
              <ArchiveRestore className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

function MedicalHistoryDialog({ email, existing, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    title: existing?.title || "",
    category: existing?.category || "condition",
    onset_date: existing?.onset_date || "",
    status: existing?.status || "active",
    severity: existing?.severity || "",
    treating_clinician: existing?.treating_clinician || "",
    exercise_implications: existing?.exercise_implications || "",
    notes: existing?.notes || "",
    source: existing?.source || "client_reported",
    last_confirmed_date: existing?.last_confirmed_date || "",
  }));
  const [saving, setSaving] = useState(false);
  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function save() {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        severity: form.severity || null,
        onset_date: form.onset_date || null,
        last_confirmed_date: form.last_confirmed_date || null,
      };
      if (existing?.id) {
        await api.patch(`/medical-history/${existing.id}`, payload);
      } else {
        await api.post(`/clients/${encodeURIComponent(email)}/medical-history`, payload);
      }
      toast.success(existing ? "Updated" : "Added");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="mh-dialog">
        <DialogHeader>
          <DialogTitle>{existing ? "Edit history entry" : "Add history entry"}</DialogTitle>
          <DialogDescription>
            Condition, surgery, fracture, fall, or any clinically-relevant event with onset,
            status and any exercise implications.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Title</Label>
            <Input
              value={form.title}
              onChange={change("title")}
              placeholder="e.g. Left knee TKR, Colles fracture, Falls in past 6 months"
              className="mt-1"
              data-testid="mh-field-title"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Category</Label>
              <select
                value={form.category}
                onChange={change("category")}
                className="mt-1 w-full h-9 px-2 border border-zinc-300 rounded-md text-sm bg-white"
                data-testid="mh-field-category"
              >
                {CATEGORIES.map((c) => (<option key={c.value} value={c.value}>{c.label}</option>))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <select
                value={form.status}
                onChange={change("status")}
                className="mt-1 w-full h-9 px-2 border border-zinc-300 rounded-md text-sm bg-white"
                data-testid="mh-field-status"
              >
                <option value="active">Active</option>
                <option value="monitoring">Monitoring</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Onset date</Label>
              <Input type="date" value={form.onset_date || ""} onChange={change("onset_date")} className="mt-1" data-testid="mh-field-onset" />
            </div>
            <div>
              <Label className="text-xs">Severity</Label>
              <select
                value={form.severity || ""}
                onChange={change("severity")}
                className="mt-1 w-full h-9 px-2 border border-zinc-300 rounded-md text-sm bg-white"
                data-testid="mh-field-severity"
              >
                <option value="">—</option>
                <option value="mild">Mild</option>
                <option value="moderate">Moderate</option>
                <option value="severe">Severe</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Source</Label>
              <select
                value={form.source}
                onChange={change("source")}
                className="mt-1 w-full h-9 px-2 border border-zinc-300 rounded-md text-sm bg-white"
                data-testid="mh-field-source"
              >
                {SOURCES.map((s) => (<option key={s.value} value={s.value}>{s.label}</option>))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Last confirmed</Label>
              <Input type="date" value={form.last_confirmed_date || ""} onChange={change("last_confirmed_date")} className="mt-1" data-testid="mh-field-confirmed" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Treating clinician (optional)</Label>
            <Input
              value={form.treating_clinician}
              onChange={change("treating_clinician")}
              placeholder="e.g. Dr Smith, City Orthopaedic"
              className="mt-1"
              data-testid="mh-field-clinician"
            />
          </div>
          <div>
            <Label className="text-xs">Exercise implications</Label>
            <Textarea
              rows={2}
              value={form.exercise_implications}
              onChange={change("exercise_implications")}
              placeholder="What this means for programming: e.g. no loaded spinal flexion; supervised balance work only"
              className="mt-1"
              data-testid="mh-field-exercise-impl"
            />
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea
              rows={2}
              value={form.notes}
              onChange={change("notes")}
              placeholder="Additional clinical detail"
              className="mt-1"
              data-testid="mh-field-notes"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="mh-save-btn">
            {saving ? "Saving…" : (existing ? "Save changes" : "Add entry")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
